import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Empty, Modal, Popconfirm, Spin, Tag, Typography, message } from "antd";
import {
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    QrcodeOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import SelectionFormFields from "../../components/selection/SelectionFormFields";
import SelectionSharePanel from "../../components/selection/SelectionSharePanel";
import copyToClipboard from "../../utils/copy";
import { buildSelectionUrl } from "../../utils/selectionToken";
import { deleteSelection, listSelections, updateSelection } from "../../utils/selectionAdminApi";
import { selectionToFormValues, validateSelectionForm } from "../../utils/selectionForm";
import { useTranslation } from "../../i18n";
import "./selection.css";

const { Paragraph, Text } = Typography;

function SelectionManage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selections, setSelections] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [editing, setEditing] = useState(null);
    const [posterItem, setPosterItem] = useState(null);
    const [editError, setEditError] = useState("");
    const [saving, setSaving] = useState(false);

    const loadSelections = useCallback(async () => {
        setLoadingList(true);
        try {
            const items = await listSelections();
            setSelections(items);
        } catch {
            message.error(t("selection.admin.manageLoadFailed"));
        } finally {
            setLoadingList(false);
        }
    }, [t]);

    useEffect(() => {
        loadSelections();
    }, [loadSelections]);

    const handleCopy = useCallback(
        async (id) => {
            try {
                await copyToClipboard(buildSelectionUrl(id));
                message.success(t("selection.admin.linkCopied"));
            } catch {
                message.error(t("selection.admin.copyFailed"));
            }
        },
        [t]
    );

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteSelection(id);
                message.success(t("selection.admin.deleteDone"));
                loadSelections();
            } catch {
                message.error(t("selection.admin.deleteFailed"));
            }
        },
        [loadSelections, t]
    );

    const openEdit = useCallback((item) => {
        setEditError("");
        setEditing({ id: item.id, item, values: selectionToFormValues(item) });
    }, []);

    const handleEditField = useCallback((field, value) => {
        setEditing((prev) =>
            prev ? { ...prev, values: { ...prev.values, [field]: value } } : prev
        );
    }, []);

    const handleSave = useCallback(async () => {
        if (!editing) {
            return;
        }
        setEditError("");

        let normalized;
        try {
            normalized = validateSelectionForm(editing.values, t, {
                allowExpiredExpiry: Boolean(editing.item?.expired),
            });
        } catch (err) {
            setEditError(err.message || t("selection.admin.editFailed"));
            return;
        }

        setSaving(true);
        try {
            await updateSelection(editing.id, {
                albumPath: normalized.albumPath,
                albumName: normalized.albumName,
                expiry: normalized.expiry,
                consent: normalized.consent,
                downloadAllType: normalized.downloadAllType,
                downloadAllToken: normalized.downloadAllToken,
                archiveMarkdown: normalized.archiveMarkdown,
            });
            message.success(t("selection.admin.editDone"));
            setEditing(null);
            loadSelections();
        } catch (err) {
            setEditError(err.message || t("selection.admin.editFailed"));
        } finally {
            setSaving(false);
        }
    }, [editing, loadSelections, t]);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("selection.admin.manageTitle")}</span>
                </div>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("selection.admin.manageSubtitle")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <div className="delivery-admin-actions" style={{ marginBottom: "1rem" }}>
                        <Button
                            icon={<UnorderedListOutlined />}
                            onClick={() => navigate("/s/results")}
                        >
                            {t("selection.admin.viewResults")}
                        </Button>
                    </div>

                    {loadingList ? (
                        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                            <Spin />
                        </div>
                    ) : selections.length === 0 ? (
                        <Empty description={t("selection.admin.manageEmpty")} />
                    ) : (
                        <div className="delivery-admin-list">
                            {selections.map((item) => (
                                <div key={item.id} className="delivery-admin-list__item">
                                    <div className="delivery-admin-list__main">
                                        <div className="delivery-admin-list__title">
                                            <Text strong>
                                                {item.albumName ||
                                                    t("selection.gallery.defaultAlbumName")}
                                            </Text>
                                            <Tag color={item.expired ? "default" : "green"}>
                                                {item.expired
                                                    ? t("selection.admin.expiredTag")
                                                    : t("selection.admin.validTag")}
                                            </Tag>
                                        </div>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {item.albumPath}
                                        </Text>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {t("selection.admin.expiresAt", {
                                                time: dayjs(item.expiry * 1000).format(
                                                    "YYYY-MM-DD HH:mm"
                                                ),
                                            })}
                                        </Text>
                                        <Text type="secondary" className="delivery-admin-list__meta">
                                            {t("selection.admin.viewCount", {
                                                count: item.viewCount ?? 0,
                                            })}
                                        </Text>
                                    </div>
                                    <div className="delivery-admin-list__actions">
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openEdit(item)}
                                        >
                                            {t("selection.admin.edit")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopy(item.id)}
                                        >
                                            {t("selection.admin.copyLink")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<QrcodeOutlined />}
                                            onClick={() => setPosterItem(item)}
                                        >
                                            {t("selection.admin.getPoster")}
                                        </Button>
                                        <Popconfirm
                                            title={t("selection.admin.deleteConfirm")}
                                            okText={t("selection.admin.deleteOk")}
                                            cancelText={t("selection.admin.deleteCancel")}
                                            onConfirm={() => handleDelete(item.id)}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                open={Boolean(editing)}
                title={t("selection.admin.editTitle")}
                onCancel={() => !saving && setEditing(null)}
                footer={[
                    <Button key="cancel" disabled={saving} onClick={() => setEditing(null)}>
                        {t("selection.admin.deleteCancel")}
                    </Button>,
                    <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                        {t("selection.admin.save")}
                    </Button>,
                ]}
                width={560}
                destroyOnClose
            >
                {editing && (
                    <>
                        <Paragraph type="secondary" style={{ fontSize: "0.82rem" }}>
                            ID: {editing.id}
                        </Paragraph>
                        <SelectionFormFields values={editing.values} onChange={handleEditField} />
                        {editError && <Paragraph type="danger">{editError}</Paragraph>}
                    </>
                )}
            </Modal>

            <Modal
                open={Boolean(posterItem)}
                title={t("selection.admin.posterTitle")}
                onCancel={() => setPosterItem(null)}
                footer={null}
                width={560}
                destroyOnClose
            >
                {posterItem && (
                    <>
                        <Paragraph type="secondary" style={{ marginBottom: "1rem" }}>
                            {t("selection.admin.posterHint")}
                        </Paragraph>
                        <SelectionSharePanel
                            url={buildSelectionUrl(posterItem.id)}
                            albumName={
                                posterItem.albumName || t("selection.gallery.defaultAlbumName")
                            }
                            expiresAtLabel={dayjs(posterItem.expiry * 1000).format(
                                "YYYY-MM-DD HH:mm"
                            )}
                            compact
                        />
                    </>
                )}
            </Modal>

            <Footer />
        </>
    );
}

export default SelectionManage;
